import { zodResolver } from "@hookform/resolvers/zod";
import { startAuthentication } from "@simplewebauthn/browser";
import { Fingerprint, KeyRound, Mail } from "lucide-react";
import * as React from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { useTurnstile } from "@/components/common/turnstile";
import { authApi, passkeyApi } from "@/lib/api/endpoints";
import { errorMessage, isApiError } from "@/lib/api/errors";
import { applyApiError, safeInternalPath } from "@/lib/form-errors";
import { useAuth } from "@/providers/auth-provider";
import { AuthLayout } from "./auth-layout";

const schema = z.object({
  email: z.string().min(1, "Enter your email").email("That doesn't look like an email"),
  password: z.string().min(1, "Enter your password"),
});

type Values = z.infer<typeof schema>;

export default function LoginPage() {
  const { adoptSession } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeInternalPath(params.get("next"));
  const turnstile = useTurnstile("login");
  const [passkeyBusy, setPasskeyBusy] = React.useState(false);
  const [passkeyError, setPasskeyError] = React.useState<string | null>(null);

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    form.clearErrors("root");
    if (turnstile.required && !turnstile.token) {
      form.setError("root", { message: "Please complete the security check." });
      return;
    }

    try {
      const session = await authApi.login({
        email: values.email.trim(),
        password: values.password,
        platform: "web",
        deviceName: navigator.userAgent.slice(0, 80),
        turnstileToken: turnstile.token ?? "",
      });
      await adoptSession(session);
      navigate(next, { replace: true });
    } catch (error) {
      // A Turnstile token is single-use: any failure means a fresh one is needed
      // before the next attempt, including a plain validation failure.
      turnstile.reset();

      if (isApiError(error) && error.code === "email_not_verified") {
        navigate(`/verify-email?email=${encodeURIComponent(values.email.trim())}`, { replace: true });
        return;
      }
      // Wrong password and unknown email are byte-identical by design — don't
      // try to tell the user which it was.
      applyApiError(error, form);
    }
  });

  const signInWithPasskey = async () => {
    setPasskeyError(null);
    setPasskeyBusy(true);
    try {
      // No email: usernameless sign-in, so the browser shows the account picker.
      const { options, challengeId } = await passkeyApi.loginOptions({});
      const response = await startAuthentication({
        optionsJSON: options as Parameters<typeof startAuthentication>[0]["optionsJSON"],
      });
      const session = await passkeyApi.loginVerify({ challengeId, response, platform: "web" });
      await adoptSession(session);
      navigate(next, { replace: true });
    } catch (error) {
      if (error instanceof DOMException && (error.name === "NotAllowedError" || error.name === "AbortError")) {
        setPasskeyError(null); // the user dismissed the prompt
      } else {
        setPasskeyError(errorMessage(error, "Passkey sign-in didn't work."));
      }
    } finally {
      setPasskeyBusy(false);
    }
  };

  const rootError = form.formState.errors.root?.message;

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Sign in to pick up where you left off."
      footer={
        <>
          New to Rocketeer Orbit?{" "}
          <Link to="/signup" className="font-medium text-primary hover:underline">
            Create an account
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        {rootError && (
          <Alert variant="destructive">
            <KeyRound />
            <AlertDescription>{rootError}</AlertDescription>
          </Alert>
        )}

        <Field label="Email" htmlFor="email" error={form.formState.errors.email?.message}>
          <Input
            id="email"
            type="email"
            autoComplete="username webauthn"
            autoFocus
            placeholder="you@company.co.uk"
            aria-invalid={Boolean(form.formState.errors.email)}
            {...form.register("email")}
          />
        </Field>

        <Field
          label="Password"
          htmlFor="password"
          error={form.formState.errors.password?.message}
          action={
            <Link to="/forgot-password" className="text-xs font-medium text-primary hover:underline">
              Forgot password?
            </Link>
          }
        >
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            aria-invalid={Boolean(form.formState.errors.password)}
            {...form.register("password")}
          />
        </Field>

        {turnstile.widget}

        <Button type="submit" className="w-full" size="lg" loading={form.formState.isSubmitting}>
          <Mail className="size-4" />
          Sign in
        </Button>
      </form>

      <div className="my-6 flex items-center gap-3">
        <Separator className="flex-1" />
        <span className="text-xs tracking-wide text-muted-foreground uppercase">or</span>
        <Separator className="flex-1" />
      </div>

      <Button
        type="button"
        variant="outline"
        size="lg"
        className="w-full"
        onClick={signInWithPasskey}
        loading={passkeyBusy}
      >
        <Fingerprint className="size-4" />
        Use a passkey
      </Button>
      {passkeyError && <p className="mt-2 text-xs text-destructive">{passkeyError}</p>}
    </AuthLayout>
  );
}
